import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { SdkHandlersProvider } from '@wix/thunderbolt-symbols'
import { FeatureExportsSymbol, PageFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { DomSelectorsSymbol } from 'feature-dom-selectors'
import type { IDomSelectors } from 'feature-dom-selectors'
import { name } from '../symbols'
import type { EffectTriggersSdkHandlers, InteractionsPageConfig } from '../types'
import { getFullId } from '@wix/thunderbolt-commons'
import type { IFeatureExportsStore } from 'thunderbolt-feature-exports'
import type { IInteractElement } from '@wix/interact'
import { TRIGGERS_AND_REACTIONS_NAME } from '../constants'

export const interactionsSdkProvider = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(
			FeatureExportsSymbol,
			TRIGGERS_AND_REACTIONS_NAME /* TODO: This is for backward compatibility. To change the name, we also need to update EE code */
		),
		DomSelectorsSymbol,
	],
	(
		pageConfig: InteractionsPageConfig,
		interactionsExports: IFeatureExportsStore<typeof TRIGGERS_AND_REACTIONS_NAME>,
		domSelectors: IDomSelectors
	): SdkHandlersProvider<EffectTriggersSdkHandlers> => {
		const getIdByCompIdAndName = (compId: string, effectName: string) => {
			const _compId = getFullId(compId)
			return pageConfig.compEffectsToVariantId[_compId]?.[effectName]
		}

		let activeEffectsChangeListener: (componentId: string, effects: Array<string>) => void = () => {}

		const getClosestWixInteractElement = (compId: string) => {
			const element = domSelectors.getByCompId(compId)
			if (!element) {
				return null
			}

			return element.closest('interact-element') as IInteractElement
		}

		const toggleEffects = (compId: string, effects: Array<string>) => {
			const wixInteractElement = getClosestWixInteractElement(compId)
			if (!wixInteractElement) {
				return
			}

			effects.forEach((effect) => {
				wixInteractElement.toggleEffect(getIdByCompIdAndName(compId, effect), 'toggle')
			})
			notifyComponentActiveStatesChange(compId)
		}

		const removeAllEffects = (compId: string) => {
			const wixInteractElement = getClosestWixInteractElement(compId)
			if (!wixInteractElement) {
				return
			}

			wixInteractElement.toggleEffect('', 'clear')
			notifyComponentActiveStatesChange(compId)
		}

		const applyEffects = (compId: string, effects: Array<string>) => {
			const wixInteractElement = getClosestWixInteractElement(compId)
			if (!wixInteractElement) {
				return
			}

			effects.forEach((effect) => {
				wixInteractElement.toggleEffect(getIdByCompIdAndName(compId, effect), 'add')
			})
			notifyComponentActiveStatesChange(compId)
		}

		const removeEffects = (compId: string, effects: Array<string>) => {
			const wixInteractElement = getClosestWixInteractElement(compId)
			if (!wixInteractElement) {
				return
			}

			effects.forEach((effect) => {
				wixInteractElement.toggleEffect(getIdByCompIdAndName(compId, effect), 'remove')
			})
			notifyComponentActiveStatesChange(compId)
		}

		const registerToActiveEffectsChange = (callback: (componentId: string, effects: Array<string>) => void): void => {
			activeEffectsChangeListener = callback
		}

		const notifyComponentActiveStatesChange = (componentId: string) => {
			const wixInteractElement = getClosestWixInteractElement(componentId)
			if (!wixInteractElement) {
				return
			}

			// @ts-expect-error TODO: need to implement getActiveEffects
			const variantIds: Array<string> = wixInteractElement.getActiveEffects()
			activeEffectsChangeListener(
				componentId,
				variantIds.map((variantId) => pageConfig.variantIdToEffect[variantId])
			)
		}

		interactionsExports.export({
			applyEffects,
			removeEffects,
		})

		return {
			getSdkHandlers: () => ({
				[name]: {
					toggleEffects,
					removeAllEffects,
					applyEffects,
					removeEffects,
					registerToActiveEffectsChange,
				},
			}),
		}
	}
)
